import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Loader2, Lock, Flag, CheckCircle2, UserX, Ban } from "lucide-react";
import api from "../lib/api";

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

const REASON_STYLES = {
  spam: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  harassment: "bg-red-500/10 text-red-400 border-red-500/20",
  inappropriate: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  other: "bg-muted/40 text-muted-foreground border-border/60",
};

const RESOLVE_ACTIONS = {
  dismiss: {
    label: "Dismiss",
    done: "Report dismissed",
    title: "Dismiss this report?",
    desc: "The report is marked as resolved and no action is taken against the content or its author.",
  },
  remove_content: {
    label: "Remove content",
    done: "Content removed",
    title: "Remove the reported content?",
    desc: "The reported content will be permanently deleted and the report marked as resolved. This cannot be undone.",
  },
  ban_user: {
    label: "Ban author",
    done: "Author banned",
    title: "Ban the content author?",
    desc: "The author's account will be banned and the report marked as resolved. They will no longer be able to sign in.",
  },
};

function userLabel(u) {
  if (!u) return "Unknown";
  if (u.username) return `@${u.username}`;
  return u.email || u.name || "Unknown";
}

export default function AdminModerationPage() {
  // Tolerate a missing provider (unit tests) the same way PostCard does.
  const auth = useAuth();
  const user = auth?.user || null;
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("open"); // "open" | "resolved" | "banned"
  const [reports, setReports] = useState([]);
  const [bannedUsers, setBannedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // confirm: { type: "resolve", report, action } | { type: "unban", target }
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = useCallback(async (which) => {
    setLoading(true);
    try {
      if (which === "banned") {
        const r = await api.get("/admin/users?banned=true&page=1&limit=50");
        setBannedUsers(r.data?.users || []);
      } else {
        const r = await api.get(`/admin/reports?status=${which}&page=1&limit=50`);
        setReports(r.data?.reports || []);
      }
    } catch { toast.error("Failed to load moderation data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (isAdmin) load(tab);
  }, [isAdmin, tab, load]);

  const handleConfirm = async () => {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    try {
      if (confirm.type === "unban") {
        await api.post(`/admin/users/${confirm.target.id}/unban`);
        // Optimistic removal, then a quiet refresh to stay in sync.
        setBannedUsers((prev) => prev.filter((u) => u.id !== confirm.target.id));
        toast.success(`Unbanned ${confirm.target.email || confirm.target.name}`);
      } else {
        await api.post(`/admin/reports/${confirm.report.id}/resolve`, { action: confirm.action });
        setReports((prev) => prev.filter((r) => r.id !== confirm.report.id));
        toast.success(RESOLVE_ACTIONS[confirm.action].done);
      }
      setConfirm(null);
      load(tab);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Action failed");
    } finally { setConfirmBusy(false); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="admin-moderation-page">
        <div className="text-center animate-fade-in-up bg-card border border-border/60 rounded-2xl p-10 shadow-sm max-w-sm" data-testid="admins-only">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-cyan-500/10 flex items-center justify-center"><Lock className="w-6 h-6 text-cyan-400" /></div>
          <h1 className="font-outfit text-lg font-semibold text-foreground mb-1">Admins only</h1>
          <p className="text-sm text-muted-foreground mb-5">This area is reserved for platform moderators. If you think you should have access, contact an administrator.</p>
          <Button onClick={() => navigate("/feed")} variant="outline" className="border-border text-foreground/80 bg-card press-down">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Feed
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "open", label: "Open reports", icon: Flag },
    { id: "resolved", label: "Resolved", icon: CheckCircle2 },
    { id: "banned", label: "Banned users", icon: UserX },
  ];

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="admin-moderation-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer" data-testid="moderation-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <ShieldCheck className="w-4 h-4 text-cyan-400" />
        <h1 className="font-outfit text-base font-semibold text-foreground">Moderation</h1>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex border-b border-border/60 mb-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium border-b-2 transition-colors bg-transparent cursor-pointer border-0 ${
                tab === t.id ? "border-b-indigo-500 text-cyan-400" : "border-b-transparent text-muted-foreground hover:text-foreground/80"
              }`}
              data-testid={`tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>
        ) : tab === "banned" ? (
          /* ─── Banned users ─── */
          bannedUsers.length === 0 ? (
            <div className="bg-card rounded-xl border border-border/60 p-12 text-center" data-testid="empty-banned">
              <UserX className="w-6 h-6 text-muted-foreground/60 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-1">No banned users</p>
              <p className="text-xs text-muted-foreground/70">Accounts you ban will show up here.</p>
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl overflow-hidden" data-testid="banned-list">
              {bannedUsers.map((u, i) => (
                <div key={u.id} className={`flex items-center gap-3 px-5 py-4 ${i > 0 ? "border-t border-border/40" : ""}`} data-testid={`banned-row-${u.id}`}>
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0"><Ban className="w-4 h-4 text-red-400" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{u.name || u.email}</p>
                    <p className="text-xs text-muted-foreground/70 truncate">{u.email}</p>
                    {u.ban_reason && <p className="text-[11px] text-muted-foreground mt-0.5 truncate">Reason: {u.ban_reason}</p>}
                  </div>
                  <span className="text-[10px] text-muted-foreground/70 flex-shrink-0">{u.banned_at ? `banned ${formatRelativeTime(u.banned_at)}` : ""}</span>
                  <Button onClick={() => setConfirm({ type: "unban", target: u })} variant="outline" className="border-border text-foreground/80 bg-card h-8 text-xs px-3 rounded-lg press-down flex-shrink-0" data-testid={`unban-${u.id}`}>
                    Unban
                  </Button>
                </div>
              ))}
            </div>
          )
        ) : (
          /* ─── Reports (open / resolved) ─── */
          reports.length === 0 ? (
            <div className="bg-card rounded-xl border border-border/60 p-12 text-center" data-testid={`empty-${tab}`}>
              {tab === "open" ? (
                <>
                  <CheckCircle2 className="w-6 h-6 text-emerald-300 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-1">No open reports</p>
                  <p className="text-xs text-muted-foreground/70">All clear — nothing needs your attention right now.</p>
                </>
              ) : (
                <>
                  <Flag className="w-6 h-6 text-muted-foreground/60 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground mb-1">No resolved reports yet</p>
                  <p className="text-xs text-muted-foreground/70">Reports you resolve will show up here.</p>
                </>
              )}
            </div>
          ) : (
            <div className="bg-card border border-border/60 rounded-xl overflow-x-auto">
              <table className="w-full text-left" data-testid="reports-table">
                <thead>
                  <tr className="border-b border-border/60">
                    <th className="px-4 py-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Reporter</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Target</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">Age</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-muted-foreground/70 uppercase tracking-wider">{tab === "open" ? "Actions" : "Resolution"}</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((r) => (
                    <tr key={r.id} className="border-b border-border/40 last:border-b-0 align-top" data-testid={`report-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-foreground">{userLabel(r.reporter)}</p>
                        {r.reporter?.email && r.reporter?.username && <p className="text-[10px] text-muted-foreground/70">{r.reporter.email}</p>}
                      </td>
                      <td className="px-4 py-3 max-w-[260px]">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-medium capitalize">{r.target_type}</span>
                          {!r.target?.exists && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted/70 text-muted-foreground">deleted</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 break-words">{r.target?.content || "(no content)"}</p>
                        <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                          by {userLabel(r.target_author)}{r.target_author?.is_banned ? " (banned)" : ""}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium capitalize ${REASON_STYLES[r.reason] || REASON_STYLES.other}`}>{r.reason}</span>
                        {r.details && <p className="text-[10px] text-muted-foreground/70 mt-1 max-w-[160px] break-words">{r.details}</p>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-xs text-muted-foreground/70">{formatRelativeTime(r.created_at)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {tab === "open" ? (
                          <div className="flex flex-col gap-1.5 items-start">
                            <button
                              onClick={() => setConfirm({ type: "resolve", report: r, action: "dismiss" })}
                              className="text-[11px] px-2.5 py-1 rounded-lg border border-border text-muted-foreground bg-card hover:bg-white/5 cursor-pointer transition-colors"
                              data-testid={`dismiss-${r.id}`}
                            >
                              Dismiss
                            </button>
                            <button
                              onClick={() => setConfirm({ type: "resolve", report: r, action: "remove_content" })}
                              disabled={!r.target?.exists}
                              className="text-[11px] px-2.5 py-1 rounded-lg border border-orange-500/30 text-orange-400 bg-orange-500/10 hover:bg-orange-500/15 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title={!r.target?.exists ? "Content no longer exists" : undefined}
                              data-testid={`remove-${r.id}`}
                            >
                              Remove content
                            </button>
                            <button
                              onClick={() => setConfirm({ type: "resolve", report: r, action: "ban_user" })}
                              disabled={r.target_author?.is_banned}
                              className="text-[11px] px-2.5 py-1 rounded-lg border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/15 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              title={r.target_author?.is_banned ? "Author is already banned" : undefined}
                              data-testid={`ban-${r.id}`}
                            >
                              Ban author
                            </button>
                          </div>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium capitalize whitespace-nowrap">
                            {(r.resolution || "resolved").replace(/_/g, " ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Confirmation Modal */}
      {confirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirm(null)} data-testid="confirm-modal">
          <div className="bg-card rounded-2xl shadow-xl max-w-sm w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-outfit text-lg font-semibold text-foreground mb-2">
              {confirm.type === "unban" ? `Unban ${confirm.target.email || confirm.target.name}?` : RESOLVE_ACTIONS[confirm.action].title}
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              {confirm.type === "unban"
                ? "This account will regain full access to the platform."
                : RESOLVE_ACTIONS[confirm.action].desc}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(null)} className="px-4 py-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer">Cancel</button>
              <Button
                onClick={handleConfirm}
                disabled={confirmBusy}
                className={`text-xs px-5 h-9 rounded-lg text-white disabled:opacity-40 press-down ${
                  confirm.type === "resolve" && confirm.action !== "dismiss" ? "bg-red-500 hover:bg-red-600" : "bg-indigo-500 hover:bg-indigo-600"
                }`}
                data-testid="confirm-action-btn"
              >
                {confirmBusy ? "Working..." : confirm.type === "unban" ? "Unban" : RESOLVE_ACTIONS[confirm.action].label}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
